/**
 * El SIGPAC de cada nave (tabla `greenhouse_sigpac_recintos`, CQ1 del cuaderno):
 * la lista «SIGPAC de las naves» de Ajustes → Empresa.
 *
 *   - Buscar: pregunta a la edge `sigpac-proxy` (acción `recintos_nave`: WMS en
 *     el centroide y en puntos DENTRO de la nave, G12), aplica la regla de la
 *     propuesta (`sigpacPropuesta.ts`, 27-09-2026) y guarda SIN confirmar el
 *     recinto propuesto o, si hay dudas, los candidatos. Lo que había sin
 *     confirmar de esa nave se sustituye, SALVO lo elegido al dibujar la nave
 *     (fuente `disenador`, 27-09-2026): eso se queda y, si el SIGPAC da ese mismo
 *     recinto, solo se le apuntan sus puntos. Es lo mismo que hace el servidor al
 *     publicar una nave y el relleno (`_shared/sigpacNaveAuto.ts`).
 *   - Confirmar la propuesta o lo ELEGIDO (`confirmarRecintos`): se quitan los
 *     demás candidatos sin confirmar y se confirma lo elegido.
 *   - Confirmar (todas sus filas): lo de antes, para una nave con filas mezcladas.
 *     El cuaderno solo imprime lo confirmado.
 *   - Escribir a mano: un recinto que teclea el admin entra ya confirmado
 *     (`escribirRecintoManual`, la misma escritura que la tarjeta del asistente).
 *   - Quitar: una fila suelta (el camino de al lado que cazó un punto).
 *
 * Solo escribe el admin (RLS por `user_roles`). Las reglas puras viven en
 * `src/lib/cuaderno/sigpacNaves.ts`.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { naturalCompare } from "@/lib/sorting";
import {
  codigosDe,
  consultaDeNave,
  estadoSigpacNave,
  CONFLICTO_RECINTO,
  FUENTE_DISENADOR,
  referenciaSigpac,
  escribirRecintoManual,
  filaDesdeWms,
  type DatosRecintoSigpac,
  type EstadoSigpacNave,
  type RecintoGuardado,
  type RecintoWms,
} from "@/lib/cuaderno/sigpacNaves";
import { aGuardar, areaAnilloHa, proponerRecinto, type EstadoPropuesta } from "@/lib/cuaderno/sigpacPropuesta";
import type { SigpacCodes } from "@/lib/cuaderno/validators";
import { errorSinDatos } from "@/hooks/useDatosExplotacion";

export const SIGPAC_NAVES_KEY = "greenhouse-sigpac-recintos";

export interface NaveSigpac {
  id: string;
  nombre: string;
  locationId: string | null;
  finca: string | null;
  /** null = sin dibujo ni centroide: solo se puede escribir a mano. */
  consulta: ReturnType<typeof consultaDeNave>;
  /** Superficie del dibujo en ha (null sin dibujo): con ella se compara el recinto. */
  areaHa: number | null;
  recintos: RecintoGuardado[];
  estado: EstadoSigpacNave<RecintoGuardado>;
}

/** Error de la búsqueda en el SIGPAC, con el código que la pantalla traduce. */
export class ErrorBusquedaSigpac extends Error {
  constructor(public readonly codigo: "sin_geometria" | "sin_respuesta" | "sin_recintos") {
    super(`sigpac: ${codigo}`);
  }
}

// Como `string` (no literal): con la ruta JSON `geometry->…` el tipado de supabase-js se va
// a una instanciación infinita (TS2589). La forma de la fila se declara abajo (FilaNave).
const COLUMNAS_NAVE: string = "id, name, location_id, centroid_lat, centroid_lng, geo:geometry->geoPolygonWgs84";

export function useSigpacNaves(enabled = true) {
  const { company } = useAuth();
  return useQuery({
    queryKey: [SIGPAC_NAVES_KEY, company?.id],
    enabled: enabled && !!company?.id,
    queryFn: async (): Promise<NaveSigpac[]> => {
      if (!company?.id) return [];
      const [naves, fincas, recintos] = await Promise.all([
        // Solo el anillo WGS84 del dibujo, no la geometría entera (bloques, perímetro…).
        supabase.from("greenhouses").select(COLUMNAS_NAVE).eq("company_id", company.id),
        supabase.from("locations").select("id, name").eq("company_id", company.id),
        supabase.from("greenhouse_sigpac_recintos").select("*").eq("company_id", company.id),
      ]);
      if (naves.error) throw errorSinDatos("greenhouses", naves.error);
      if (fincas.error) throw errorSinDatos("locations", fincas.error);
      if (recintos.error) throw errorSinDatos("greenhouse_sigpac_recintos", recintos.error);

      const nombreFinca = new Map((fincas.data ?? []).map((l) => [l.id, l.name]));
      const porNave = new Map<string, RecintoGuardado[]>();
      for (const r of (recintos.data ?? []) as RecintoGuardado[]) {
        const lista = porNave.get(r.greenhouse_id) ?? [];
        lista.push(r);
        porNave.set(r.greenhouse_id, lista);
      }
      type FilaNave = { id: string; name: string; location_id: string | null; centroid_lat: number | null; centroid_lng: number | null; geo: unknown };
      return ((naves.data ?? []) as unknown as FilaNave[])
        .map((n) => {
          const rs = porNave.get(n.id) ?? [];
          const consulta = consultaDeNave({ centroid_lat: n.centroid_lat, centroid_lng: n.centroid_lng, geometry: { geoPolygonWgs84: n.geo } });
          const areaHa = areaAnilloHa(consulta?.polygon);
          return {
            id: n.id,
            nombre: n.name,
            locationId: n.location_id,
            finca: n.location_id ? nombreFinca.get(n.location_id) ?? null : null,
            consulta,
            areaHa,
            recintos: rs,
            estado: estadoSigpacNave(rs, areaHa),
          };
        })
        .sort((a, b) => naturalCompare(a.finca ?? "", b.finca ?? "") || naturalCompare(a.nombre, b.nombre));
    },
  });
}

export function useAccionesSigpacNaves() {
  const { company, user } = useAuth();
  const qc = useQueryClient();
  const refrescar = () => qc.invalidateQueries({ queryKey: [SIGPAC_NAVES_KEY, company?.id] });

  /** Quita lo que la nave tenía SIN confirmar (lo confirmado y lo elegido al dibujarla se quedan). */
  const quitarSinConfirmar = async (naveId: string) => {
    const { error } = await supabase
      .from("greenhouse_sigpac_recintos")
      .delete()
      .eq("greenhouse_id", naveId)
      .is("confirmado_en", null)
      .neq("fuente", FUENTE_DISENADOR);
    if (error) throw errorSinDatos("greenhouse_sigpac_recintos", error);
  };

  const buscar = useMutation({
    mutationFn: async (nave: Pick<NaveSigpac, "id" | "consulta"> & { recintos?: RecintoGuardado[] }) => {
      if (!company?.id) throw new Error("sigpac: sin empresa");
      if (!nave.consulta) throw new ErrorBusquedaSigpac("sin_geometria");
      const { data, error } = await supabase.functions.invoke("sigpac-proxy", {
        body: { action: "recintos_nave", centroid: nave.consulta.centroid, polygon: nave.consulta.polygon },
      });
      const payload = data as { ok?: boolean; recintos?: RecintoWms[] } | null;
      if (error || !payload?.ok) throw new ErrorBusquedaSigpac("sin_respuesta");
      const filas = (payload.recintos ?? []).map((r) => filaDesdeWms(nave.id, company.id, r)).filter((f): f is NonNullable<typeof f> => !!f);
      if (filas.length === 0) throw new ErrorBusquedaSigpac("sin_recintos");
      // La regla: el propuesto solo, o los candidatos si hay dudas (lo mismo que el servidor).
      const propuesta = proponerRecinto(areaAnilloHa(nave.consulta.polygon), filas);
      const guardar = aGuardar(propuesta);
      await quitarSinConfirmar(nave.id);
      // Lo elegido al dibujar se queda: si el SIGPAC da ese mismo recinto, solo se le apuntan sus puntos.
      const elegidos = new Map(
        (nave.recintos ?? []).filter((r) => r.fuente === FUENTE_DISENADOR && !r.confirmado_en).map((r) => [referenciaSigpac(r), r.id]),
      );
      for (const f of guardar) {
        const id = elegidos.get(referenciaSigpac(f));
        if (!id) continue;
        const { error: upError } = await supabase.from("greenhouse_sigpac_recintos").update({ muestras: f.muestras }).eq("id", id).is("confirmado_en", null);
        if (upError) throw errorSinDatos("greenhouse_sigpac_recintos", upError);
      }
      const nuevas = guardar.filter((f) => !elegidos.has(referenciaSigpac(f)));
      // Si un recinto ya estaba confirmado en esa nave, se deja como está.
      const { error: insError } = nuevas.length
        ? await supabase.from("greenhouse_sigpac_recintos").upsert(nuevas, { onConflict: CONFLICTO_RECINTO, ignoreDuplicates: true })
        : { error: null };
      if (insError) throw errorSinDatos("greenhouse_sigpac_recintos", insError);
      return { estado: propuesta.estado as EstadoPropuesta, guardados: guardar.length };
    },
    onSettled: refrescar,
  });

  const confirmar = useMutation({
    mutationFn: async (naveIds: string[]) => {
      if (naveIds.length === 0) return;
      const { error } = await supabase
        .from("greenhouse_sigpac_recintos")
        .update({ confirmado_en: new Date().toISOString(), confirmado_por: user?.id ?? null })
        .in("greenhouse_id", naveIds)
        .is("confirmado_en", null);
      if (error) throw errorSinDatos("greenhouse_sigpac_recintos", error);
    },
    onSettled: refrescar,
  });

  /**
   * Confirmar la propuesta de una nave, o los candidatos que el admin ha ELEGIDO:
   * primero se quitan los demás candidatos sin confirmar de esa nave y luego se
   * confirma lo elegido. En ese orden: si lo segundo fallara, lo elegido sigue ahí,
   * sin confirmar, y se puede volver a confirmar.
   */
  const confirmarRecintos = useMutation({
    mutationFn: async (elecciones: { naveId: string; ids: string[]; partes?: Record<string, number> }[]) => {
      for (const { naveId, ids, partes } of elecciones) {
        if (ids.length === 0) continue;
        const quitar = await supabase
          .from("greenhouse_sigpac_recintos")
          .delete()
          .eq("greenhouse_id", naveId)
          .is("confirmado_en", null)
          .not("id", "in", `(${ids.join(",")})`);
        if (quitar.error) throw errorSinDatos("greenhouse_sigpac_recintos", quitar.error);
        const { error } = await supabase
          .from("greenhouse_sigpac_recintos")
          .update({ confirmado_en: new Date().toISOString(), confirmado_por: user?.id ?? null })
          .eq("greenhouse_id", naveId)
          .in("id", ids)
          .is("confirmado_en", null);
        if (error) throw errorSinDatos("greenhouse_sigpac_recintos", error);
        for (const id of ids) if (partes?.[id] != null) await apuntarParteNave(naveId, { id }, partes[id]);
      }
    },
    onSettled: refrescar,
  });

  /** Vuelve a «por confirmar» (para revisarla otra vez). */
  const desconfirmar = useMutation({
    mutationFn: async (naveId: string) => {
      const { error } = await supabase
        .from("greenhouse_sigpac_recintos")
        .update({ confirmado_en: null, confirmado_por: null })
        .eq("greenhouse_id", naveId);
      if (error) throw errorSinDatos("greenhouse_sigpac_recintos", error);
    },
    onSettled: refrescar,
  });

  const escribirAMano = useMutation({
    mutationFn: async ({ naveId, codes, datos, parte }: { naveId: string; codes: SigpacCodes; datos?: DatosRecintoSigpac; parte?: number | null }) => {
      if (!company?.id) throw new Error("sigpac: sin empresa");
      // La misma escritura que la tarjeta del asistente del cuaderno (CU7); con lo
      // que dijo el SIGPAC si se comprobó antes («Comprobar en el SIGPAC», 24-10-2026).
      const { error } = await escribirRecintoManual(supabase, { companyId: company.id, naveId, codes, userId: user?.id ?? null, datos });
      if (error) throw errorSinDatos("greenhouse_sigpac_recintos", error);
      if (parte != null) await apuntarParteNave(naveId, { codes }, parte);
    },
    onSettled: refrescar,
  });

  const quitarRecinto = useMutation({
    mutationFn: async (recintoId: string) => {
      const { error } = await supabase.from("greenhouse_sigpac_recintos").delete().eq("id", recintoId);
      if (error) throw errorSinDatos("greenhouse_sigpac_recintos", error);
    },
    onSettled: refrescar,
  });

  return { buscar, confirmar, confirmarRecintos, desconfirmar, escribirAMano, quitarRecinto };
}

/**
 * Apunta qué parte de la nave cae en un recinto (`parte_nave`, 25-10-2026): con
 * ella el cuaderno reparte la superficie cultivada de una nave que pisa varios.
 * Va APARTE de confirmar y no falla: sin la migración `20261025090000` la columna
 * no existe y confirmar tiene que seguir funcionando igual.
 */
async function apuntarParteNave(naveId: string, fila: { id: string } | { codes: SigpacCodes }, parte: number) {
  if (!Number.isFinite(parte) || parte < 0) return;
  let q = supabase
    .from("greenhouse_sigpac_recintos")
    .update({ parte_nave: Math.round(Math.min(1, parte) * 10000) / 10000 })
    .eq("greenhouse_id", naveId);
  if ("id" in fila) q = q.eq("id", fila.id);
  else {
    const c = fila.codes;
    q = q.eq("provincia", c.provincia).eq("municipio_codigo", c.municipio).eq("agregado", c.agregado).eq("zona", c.zona).eq("poligono", c.poligono).eq("parcela", c.parcela).eq("recinto", c.recinto);
  }
  const { error } = await q;
  if (error && !["42703", "PGRST204"].includes(error.code ?? "")) console.warn("[sigpac] parte_nave sin guardar:", error.message);
}

/** Lo que dice el SIGPAC de un recinto por su código (`sigpac-proxy` → `recinto_por_codigo`). */
export type ComprobacionSigpac =
  | { estado: "existe"; datos: DatosRecintoSigpac & { pendiente_media?: number | null }; geometry: unknown }
  | { estado: "no_existe" }
  | { estado: "sin_respuesta" };

/**
 * Comprobar un recinto en el SIGPAC (24-10-2026): ¿existe?, su uso, superficie y
 * municipio, y su dibujo. «sin_respuesta» también si la edge aún no tiene la
 * acción (contesta `ok: false`): la pantalla lo dice y deja guardar sin datos.
 */
export async function comprobarEnSigpac(codes: SigpacCodes): Promise<ComprobacionSigpac> {
  try {
    const { data, error } = await supabase.functions.invoke("sigpac-proxy", { body: { action: "recinto_por_codigo", codes } });
    const p = data as { ok?: boolean; existe?: boolean; recinto?: Record<string, unknown>; geometry?: unknown } | null;
    if (error || !p?.ok) return { estado: "sin_respuesta" };
    if (p.existe === false) return { estado: "no_existe" };
    if (!p.recinto) return { estado: "sin_respuesta" };
    const r = p.recinto;
    const num = (v: unknown) => (v != null && Number.isFinite(Number(v)) ? Number(v) : null);
    return {
      estado: "existe",
      datos: {
        municipio_nombre: typeof r.municipio_nombre === "string" ? r.municipio_nombre : null,
        uso_sigpac: typeof r.uso_sigpac === "string" ? r.uso_sigpac : null,
        superficie_ha: num(r.superficie_ha),
        coef_regadio: num(r.coef_regadio),
        pendiente_media: num(r.pendiente_media),
      },
      geometry: p.geometry ?? null,
    };
  } catch {
    return { estado: "sin_respuesta" };
  }
}

/** Lo del SIGPAC de varios recintos a la vez (los candidatos de una nave), cacheado una hora. */
export function useRecintosEnSigpac(lista: RecintoGuardado[], enabled = true) {
  return useQueries({
    queries: lista.map((r) => ({
      queryKey: ["sigpac-recinto", referenciaSigpac(r)],
      enabled,
      staleTime: 60 * 60 * 1000,
      retry: false,
      queryFn: () => comprobarEnSigpac(codigosDe(r)),
    })),
  });
}
